import { useTranslation } from 'react-i18next'
import { useLocalizedNavigate } from '@/shared/hooks'
import { Zap } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import type { SubscriptionPlan } from '@/shared/types'

// Plan limits - projects per month
const PLAN_LIMITS: Record<SubscriptionPlan, number> = {
  'one-shot': 1,
  'starter': 5,
  'pro': 10,
  'agency': 20,
}

interface QuotaBarProps {
  plan: SubscriptionPlan | undefined
  projectsUsed: number
}

export function QuotaBar({ plan, projectsUsed }: QuotaBarProps) {
  const { t } = useTranslation()
  const navigate = useLocalizedNavigate()

  // Don't show for one-shot users (no monthly quota concept)
  if (!plan || plan === 'one-shot') {
    return null
  }

  const limit = PLAN_LIMITS[plan]
  const percentage = Math.min(100, (projectsUsed / limit) * 100)
  const isNearLimit = percentage >= 80

  return (
    <div
      className={cn(
        'flex items-center justify-between rounded-lg border bg-background p-4',
        isNearLimit ? 'border-warning' : 'border-border'
      )}
    >
      {/* Left side: Icon + Plan + Usage */}
      <div className="flex items-center gap-3">
        <Zap className={cn('h-5 w-5', isNearLimit ? 'text-warning' : 'text-accent')} />
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-semibold text-foreground">
            {t(`pricing.plans.${plan}`)}
          </span>
          <span className="text-sm text-foreground-muted">
            {t('dashboard.quota.usage', { used: projectsUsed, total: limit })}
          </span>
        </div>
      </div>

      {/* Right side: Progress bar + Upgrade link */}
      <div className="flex items-center gap-4">
        {/* Progress bar */}
        <div className="h-1.5 w-28 overflow-hidden rounded-full bg-background-secondary">
          <div
            className={cn(
              'h-full rounded-full transition-all',
              isNearLimit ? 'bg-warning' : 'bg-accent'
            )}
            style={{ width: `${percentage}%` }}
          />
        </div>

        {/* Upgrade link */}
        <button
          onClick={() => navigate('/settings/billing')}
          className="text-sm font-medium text-accent transition-colors hover:text-accent-hover"
        >
          {t('dashboard.quota.upgrade')}
        </button>
      </div>
    </div>
  )
}
